import {
  Stack,
  Input,
  Button,
  CircularProgress,
  Autocomplete,
  TextArea,
} from '@ltvco/refresh-lib/theme';
import React from 'react';

import { ErrorMessageWithIcon } from 'components/shared/ErrorMessageWithIcon';

import {
  VEHICLE_TYPES,
  USAGE_TYPES,
  VehicleType,
  UsageType,
} from '../constants';

interface IPreferencesFormProps {
  budgetMin: string;
  budgetMax: string;
  vehicleType: VehicleType;
  usage: UsageType;
  description: string;
  error: string | null;
  isLoading: boolean;
  onBudgetMinChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBudgetMaxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVehicleTypeChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
  ) => void;
  onUsageChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
  ) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e?: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

/**
 * PreferencesForm - Form for entering vehicle preferences
 * @component
 */
export const PreferencesForm = ({
  budgetMin,
  budgetMax,
  vehicleType,
  usage,
  description,
  error,
  isLoading,
  onBudgetMinChange,
  onBudgetMaxChange,
  onVehicleTypeChange,
  onUsageChange,
  onDescriptionChange,
  onSubmit,
}: IPreferencesFormProps) => {
  return (
    <Stack direction="row" width="100%">
      <form onSubmit={onSubmit} style={{ width: '100%', marginLeft: '10px' }}>
        <Stack spacing={3} width={{ xs: '100%', md: '360px' }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Input
                fullWidth
                value={budgetMin}
                onChange={onBudgetMinChange}
                placeholder="Minimum Amount"
                disabled={isLoading}
              />
              <Input
                fullWidth
                value={budgetMax}
                onChange={onBudgetMaxChange}
                placeholder="Maximum Amount"
                disabled={isLoading}
              />
            </Stack>
            <Autocomplete
              fullWidth
              value={vehicleType || undefined}
              onChange={onVehicleTypeChange}
              options={[...VEHICLE_TYPES]}
              disabled={isLoading}
              renderInput={(params) => (
                <Input
                  {...params}
                  placeholder="Preferred Vehicle Type (eg, SUV, Sedan, Truck)"
                />
              )}
            />
            <Autocomplete
              fullWidth
              value={usage || undefined}
              onChange={onUsageChange}
              options={[...USAGE_TYPES]}
              disabled={isLoading}
              renderInput={(params) => (
                <Input
                  {...params}
                  placeholder="What will you use it for? (selectable)"
                />
              )}
            />
            <TextArea
              placeholder="Describe your needs: family size, commuting, lifestyle, preferences."
              rows={3}
              fullWidth
              onChange={onDescriptionChange}
              disabled={isLoading}
              value={description}
              data-testid="comment-textarea"
              sx={{ mb: 1 }}
            />
          </Stack>
          <ErrorMessageWithIcon errorMessage={error || ''} />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={!budgetMax || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            Find My Match
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};
